import React, {useState, useEffect} from 'react'
import {useTranslation} from 'react-i18next'
import classNames from 'classnames'
import {observer} from 'mobx-react'
import {useInjection} from 'dna-react-ioc'
import {Checkbox, Form, Switch, Row, Col} from 'antd'
import {usePblCommon} from '../services/'
import {
    Input,
    IconTitle,
    AmountFormField,
    AmountCurrencyFormField,
    TransactionTypeFormField
} from '@/components'
import {TerminalSelectField, ExpirationDateField} from '../../'
import {OrderNumberOptionsField} from '../OrderNumberOptionsField'
import {PaymentReferenceMode} from '@/api'
import {DEFAULT_CURRENCY, Currencies} from '@/constants/currencies'
import {INewPaymentLinkModalStore} from '@/pages/PayByLink/components'
import {getNewPaymentLinkTransactionTypes} from '@/utils/transactions'
import {PayByLinkCreateNewLinkStoreSymbol} from '@/pages/PayByLink'
import {DESCRIPTION_MAX_LENGTH} from '@/pages/PayByLink/constants'
import {MultiUseFormProps} from './props'
import {
    NEW_PAYMENT_DESCRIPTION,
    NEW_PAYMENT_RECURRING,
    SELECT_TRANSACTION_TYPE
} from '@/constants/playwright-ids'

import styles from './styles.scss'

export const MultiUseForm: React.FC<MultiUseFormProps> = observer(({form, formId, onFinish}) => {
    const store = useInjection<INewPaymentLinkModalStore>(PayByLinkCreateNewLinkStoreSymbol)
    const {t} = useTranslation()
    const paymentReferenceMode = Form.useWatch('paymentReferenceMode', form)
    const orderNumberTooltip =
        paymentReferenceMode === PaymentReferenceMode.PAYER_INPUT
            ? t('OrderNumberOptions.tooltips.multiUseOrderNumber')
            : t('MultiUseForm.tooltips.orderNumber')

    const [canCustomerSetAmount, setCanCustomerSetAmount] = useState(false)
    const [minMaxIsVisible, setMinMaxIsVisible] = useState(false)
    const [datePickerIsVisible, setDatePickerIsVisible] = useState(false)
    const [canSuggestPresetAmount, setCanSuggestPresetAmount] = useState(false)

    const {isVerificationFlag, isVerification, getTerminalData} = usePblCommon(
        form,
        store.terminals
    )

    useEffect(() => {
        if (!canCustomerSetAmount) {
            setMinMaxIsVisible(false)
            setCanSuggestPresetAmount(false)
        } else {
            form.setFieldValue('amount', 0)
        }
    }, [canCustomerSetAmount])

    useEffect(() => {
        if (isVerificationFlag) {
            setCanCustomerSetAmount(false)
            setMinMaxIsVisible(false)
            setCanSuggestPresetAmount(false)
        }
    }, [isVerificationFlag, form])

    useEffect(() => {
        if (store.terminals?.length === 1 && !form.getFieldValue('terminalId')) {
            form.setFieldsValue({terminalId: store.terminals[0].value})
        }
    }, [store.terminals, form])

    return (
        <Form
            id={formId}
            form={form}
            initialValues={{
                invoiceId: '',
                currency: DEFAULT_CURRENCY,
                paymentReferenceMode: PaymentReferenceMode.FIXED
            }}
            layout={'vertical'}
            labelCol={{span: 24}}
            wrapperCol={{span: 24}}
            onFinish={(v) => {
                onFinish({...v, type: 'multiUse', canCustomerSetAmount})
            }}
            autoComplete='off'
        >
            <TerminalSelectField options={store.terminals} />

            <Form.Item
                name='invoiceId'
                label={
                    <IconTitle
                        title={t('MultiUseForm.labels.orderNumber')}
                        tooltipText={orderNumberTooltip}
                    />
                }
                rules={[
                    {
                        required: true,
                        message: t('MultiUseForm.errors.orderNumberRequired')
                    }
                ]}
            >
                <Input placeholder={t('MultiUseForm.placeholders.invoiceId')} />
            </Form.Item>

            <OrderNumberOptionsField />
            <Form.Item shouldUpdate noStyle>
                {() =>
                    getTerminalData().card?.pblTransactionTypeSelection && (
                        <TransactionTypeFormField
                            data-test-id={SELECT_TRANSACTION_TYPE}
                            form={form}
                            transactionTypes={getNewPaymentLinkTransactionTypes()}
                        />
                    )
                }
            </Form.Item>
            <Form.Item shouldUpdate noStyle>
                {() => (
                    <Form.Item
                        hidden={!getTerminalData().card?.allowPblRecurring}
                        name='recurring'
                        valuePropName='checked'
                        data-test-id={NEW_PAYMENT_RECURRING}
                    >
                        <Checkbox>
                            <IconTitle
                                title={t('MultiUseForm.labels.recurring')}
                                tooltipText={t('MultiUseForm.tooltips.recurring')}
                            />
                        </Checkbox>
                    </Form.Item>
                )}
            </Form.Item>

            <Form.Item
                label={t('MultiUseForm.labels.description')}
                name='description'
                rules={[
                    {
                        required: true,
                        message: t('MultiUseForm.errors.descriptionRequired')
                    }
                ]}
                data-test-id={NEW_PAYMENT_DESCRIPTION}
            >
                <Input
                    maxLength={DESCRIPTION_MAX_LENGTH}
                    placeholder={t('MultiUseForm.labels.nameOfTheServiceOrItemYouProvide')}
                />
            </Form.Item>

            <div
                className={classNames(styles.switchHolder, {
                    [styles.disabled]: isVerification()
                })}
            >
                <IconTitle
                    title={t('MultiUseForm.labels.customerSetsAmount')}
                    tooltipText={t('MultiUseForm.tooltips.customerSetsAmount')}
                />

                <Switch
                    checked={canCustomerSetAmount}
                    onChange={(checked) => setCanCustomerSetAmount(checked)}
                />
            </div>

            <Form.Item shouldUpdate noStyle>
                {() => {
                    return (
                        <AmountCurrencyFormField
                            isRequired={!canCustomerSetAmount}
                            currencies={getTerminalData().supportedCurrencies}
                            form={form}
                            inputProps={{
                                disabled: isVerification() || canCustomerSetAmount
                            }}
                            shouldAcceptZero={isVerification() || canCustomerSetAmount}
                        />
                    )
                }}
            </Form.Item>

            <div
                className={classNames(styles.switchHolder, {
                    [styles.disabled]: !canCustomerSetAmount
                })}
            >
                <IconTitle
                    title={t('MultiUseForm.labels.suggestAmount')}
                    tooltipText={t('MultiUseForm.tooltips.presetAmount')}
                />

                <Switch
                    checked={canSuggestPresetAmount}
                    onChange={(checked) => setCanSuggestPresetAmount(checked)}
                />
            </div>

            {canSuggestPresetAmount && (
                <Form.Item label={t('MultiUseForm.labels.amount')} shouldUpdate>
                    {() => {
                        const isDisabled = isVerification()
                        const min = Number(form.getFieldValue('minAmount'))
                        const max = Number(form.getFieldValue('maxAmount'))

                        const shouldValidateRange =
                            minMaxIsVisible &&
                            !isDisabled &&
                            !isNaN(min) &&
                            !isNaN(max) &&
                            min < max

                        return (
                            <AmountFormField
                                name='presetAmount'
                                currency={Currencies.GBP}
                                form={form}
                                noStyle
                                inputProps={{prefix: null}}
                                shouldAcceptZero={isVerification()}
                                rules={[
                                    {
                                        validator(_, value) {
                                            if (!shouldValidateRange) {
                                                return Promise.resolve()
                                            }

                                            const amount = Number(value)

                                            if (amount < min || amount > max) {
                                                return Promise.reject(
                                                    new Error(
                                                        t('MultiUseForm.errors.amountBetween', {
                                                            min,
                                                            max
                                                        })
                                                    )
                                                )
                                            }

                                            return Promise.resolve()
                                        }
                                    }
                                ]}
                            />
                        )
                    }}
                </Form.Item>
            )}

            <div
                className={classNames(styles.switchHolder, {
                    [styles.disabled]: !canCustomerSetAmount
                })}
            >
                <IconTitle
                    title={t('MultiUseForm.labels.setMinMaxAmount')}
                    tooltipText={t('MultiUseForm.tooltips.setMinMaxAmount')}
                />

                <Switch
                    checked={minMaxIsVisible}
                    onChange={(checked) => setMinMaxIsVisible(checked)}
                />
            </div>

            {minMaxIsVisible && (
                <Row gutter={[8, 0]}>
                    <Col xs={12}>
                        <Form.Item
                            label={t('MultiUseForm.labels.minAmount')}
                            name='minAmount'
                            dependencies={['maxAmount']}
                            rules={[
                                ({getFieldValue}) => ({
                                    validator(_, value) {
                                        const min = Number(value)
                                        const max = Number(getFieldValue('maxAmount'))

                                        if (!isNaN(min) && !isNaN(max) && min >= max) {
                                            return Promise.reject(
                                                new Error(t('MultiUseForm.errors.minLessThanMax'))
                                            )
                                        }

                                        return Promise.resolve()
                                    }
                                })
                            ]}
                        >
                            <AmountFormField
                                name='minAmount'
                                currency={Currencies.GBP}
                                form={form}
                                label={null}
                                noStyle
                                inputProps={{prefix: null}}
                                amountRequiredMessage={t('MultiUseForm.errors.minAmountRequired')}
                            />
                        </Form.Item>
                    </Col>

                    <Col xs={12}>
                        <Form.Item
                            label={t('MultiUseForm.labels.maxAmount')}
                            name='maxAmount'
                            dependencies={['minAmount']}
                            rules={[
                                ({getFieldValue}) => ({
                                    validator(_, value) {
                                        const max = Number(value)
                                        const min = Number(getFieldValue('minAmount'))

                                        if (!isNaN(min) && !isNaN(max) && max <= min) {
                                            return Promise.reject(
                                                new Error(
                                                    t('MultiUseForm.errors.maxGreaterThanMin')
                                                )
                                            )
                                        }

                                        return Promise.resolve()
                                    }
                                })
                            ]}
                        >
                            <AmountFormField
                                name='maxAmount'
                                currency={Currencies.GBP}
                                form={form}
                                label={null}
                                noStyle
                                inputProps={{prefix: null}}
                                amountRequiredMessage={t('MultiUseForm.errors.maxAmountRequired')}
                            />
                        </Form.Item>
                    </Col>
                </Row>
            )}

            <div className={styles.switchHolder}>
                <IconTitle
                    title={t('MultiUseForm.labels.addExpiryDate')}
                    tooltipText={t('MultiUseForm.tooltips.addExpiryDate')}
                />

                <Switch
                    checked={datePickerIsVisible}
                    onChange={(checked) => setDatePickerIsVisible(checked)}
                />
            </div>

            <ExpirationDateField form={form} visible={datePickerIsVisible} />
        </Form>
    )
})
